import { Bot, Database, Globe, Users } from 'lucide-react';
import type { TenantOverviewDTO } from '../../types/lac';
import { LiksTooltip } from '../../components/liks/LiksTooltip/LiksTooltip';
import { CrawlStatusBar } from '../../features/lac/components/CrawlStatusBar';
import { useI18n } from '../../i18n/useI18n';
import { formatNumber } from '../../utils/formatUtils';
import { formatCountWithMax, isLimitReached } from './lacTenantRowFormatters';

export type LacTenantRowMetricsProps = {
  userCount: number;
  maxUsers: number | null | undefined;
  knowledgeBaseCount: number;
  chatbotCount: number;
  maxChatbots: number | null | undefined;
  statusCounts: NonNullable<
    NonNullable<TenantOverviewDTO['crawlJobMetrics']>['statusCounts']
  >;
};

export function LacTenantRowMetrics({
  userCount,
  maxUsers,
  knowledgeBaseCount,
  chatbotCount,
  maxChatbots,
  statusCounts,
}: LacTenantRowMetricsProps) {
  const { locale, translate } = useI18n();
  const isUserLimitReached = isLimitReached(userCount, maxUsers);
  const isChatbotLimitReached = isLimitReached(chatbotCount, maxChatbots);
  const hasCrawlNumbers = statusCounts.some((item) => (item.count ?? 0) > 0);

  const resolveStatusLabel = (status: string | null | undefined) => {
    if (!status) {
      return translate('lac.organisations.overview.crawlStatusUnknown');
    }
    const key = `knowledge.crawlStatus.${status}` as const;
    const translated = translate(key);
    return translated === key ? status : translated;
  };

  return (
    <div className="lac-tenants__metrics">
      <LiksTooltip
        content={translate('lac.organisations.list.usersLabel')}
        placement="top-end"
      >
        <span
          className="lac-tenants__metric"
          aria-label={`${translate('lac.organisations.list.usersLabel')}: ${formatCountWithMax(userCount, maxUsers, locale)}`}
        >
          <Users
            className="lac-tenants__metric-icon"
            strokeWidth={2}
            aria-hidden
          />
          <span
            className={[
              'lac-tenants__metric-value',
              'lac-tenants__metric-value--ratio',
              isUserLimitReached && 'lac-tenants__metric-value--danger',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden
          >
            {formatCountWithMax(userCount, maxUsers, locale)}
          </span>
        </span>
      </LiksTooltip>

      <LiksTooltip
        content={translate('lac.organisations.list.knowledgeBasesLabel')}
        placement="top-end"
      >
        <span
          className="lac-tenants__metric"
          aria-label={`${translate('lac.organisations.list.knowledgeBasesLabel')}: ${formatNumber(knowledgeBaseCount, locale)}`}
        >
          <Database
            className="lac-tenants__metric-icon"
            strokeWidth={2}
            aria-hidden
          />
          <span
            className="lac-tenants__metric-value lac-tenants__metric-value--count"
            aria-hidden
          >
            {formatNumber(knowledgeBaseCount, locale)}
          </span>
        </span>
      </LiksTooltip>

      <LiksTooltip
        content={translate('lac.organisations.list.chatbotsLabel')}
        placement="top-end"
      >
        <span
          className="lac-tenants__metric"
          aria-label={`${translate('lac.organisations.list.chatbotsLabel')}: ${formatCountWithMax(chatbotCount, maxChatbots, locale)}`}
        >
          <Bot
            className="lac-tenants__metric-icon"
            strokeWidth={2}
            aria-hidden
          />
          <span
            className={[
              'lac-tenants__metric-value',
              'lac-tenants__metric-value--ratio',
              isChatbotLimitReached && 'lac-tenants__metric-value--danger',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden
          >
            {formatCountWithMax(chatbotCount, maxChatbots, locale)}
          </span>
        </span>
      </LiksTooltip>

      <span className="lac-tenants__metric lac-tenants__metric--crawl">
        <Globe
          className={[
            'lac-tenants__metric-icon',
            !hasCrawlNumbers && 'lac-tenants__metric-icon--hidden',
          ]
            .filter(Boolean)
            .join(' ')}
          strokeWidth={2}
          aria-hidden
        />
        <CrawlStatusBar
          statusCounts={statusCounts}
          locale={locale}
          resolveStatusLabel={resolveStatusLabel}
          ariaLabel={translate('lac.organisations.list.crawlsLabel')}
        />
      </span>
    </div>
  );
}
